import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  InsightsProductUsageResponse,
  InsightsProductUsageRoleFilter,
  InsightsProductUsageWindow,
} from "@shared-core/data/api-types";
import {
  ROLE_FILTER_LABELS,
  ROLE_FILTER_OPTIONS,
  WINDOW_LABELS,
  WINDOW_OPTIONS,
  freshnessLabel,
} from "@shared-core/domain/insights-product-usage";
import { roleLabel as businessRoleLabel } from "@shared-core/domain/roleLabels";
import { formatCount } from "@shared-core/domain/formatters";
import { StatCard } from "../../patterns/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { ToggleGroup, ToggleGroupItem } from "../../primitives/toggle-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { ProductUsagePagesChart } from "./ProductUsagePagesChart";
import { ProductUsageDailyChart } from "./ProductUsageDailyChart";

// Insights → Product Usage: who is using the CIS, and for what. Reads the
// composed /views/insights/product-usage endpoint over the two dbt
// product-usage metrics — staff-first (product owner: "the same as Lauren's
// activity view but all staff, then sales rep and CSR/AM filters"), in this
// fixed order:
//   1. Role comparison tiles — sales reps vs CSRs vs the wider team. ALWAYS
//      covers everyone, regardless of the role filter below — see the
//      section's own note.
//   2. One staff table, sorted by page views descending (server-side),
//      narrowed by the role filter.
//   3. What's opened — page views per route, narrowed by the role filter.
//   4. Activity by day, narrowed by the role filter.
// The role filter drives everything except the tiles: changing it refetches
// from the server rather than filtering the returned arrays client-side.
// Named-individual activity, gated on Permit.INSIGHTS_MANAGEMENT at the
// route (admin/manager only) — see spec §9.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; usage: InsightsProductUsageResponse };

export function ProductUsageTab() {
  const adapter = useDataAdapter();
  const [selectedWindow, setSelectedWindow] =
    useState<InsightsProductUsageWindow>("this_month");
  const [selectedRole, setSelectedRole] =
    useState<InsightsProductUsageRoleFilter>("all");
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const usage = await adapter.getInsightsProductUsage(
        selectedWindow,
        selectedRole,
      );
      setState({ status: "ready", usage });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, selectedWindow, selectedRole]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={selectedWindow}
          onValueChange={(v) =>
            setSelectedWindow(v as InsightsProductUsageWindow)
          }
        >
          <SelectTrigger className="w-44" aria-label="Window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((w) => (
              <SelectItem key={w} value={w}>
                {WINDOW_LABELS[w]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          aria-label="Staff filter"
          value={selectedRole}
          onValueChange={(v) => {
            // Radix emits "" when the active item is clicked again: keep it.
            if (!v) return;
            setSelectedRole(v as InsightsProductUsageRoleFilter);
          }}
        >
          {ROLE_FILTER_OPTIONS.map((r) => (
            <ToggleGroupItem key={r} value={r} className="px-3 text-xs">
              {ROLE_FILTER_LABELS[r]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {state.status === "ready" && (
          <span className="text-muted-foreground ml-auto text-xs">
            {freshnessLabel(state.usage.latest_activity_date)} · the usage data
            refreshes once a day, not live
          </span>
        )}
      </div>

      {state.status === "loading" && (
        <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
          Loading product usage…
        </div>
      )}
      {state.status === "error" && (
        <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
          <p className="text-destructive text-sm">
            Couldn’t load product usage. Please try again.
          </p>
          <button
            type="button"
            onClick={load}
            className="text-primary text-sm underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
      {state.status === "ready" && <ProductUsageSections usage={state.usage} />}
    </div>
  );
}

function ProductUsageSections({
  usage,
}: {
  usage: InsightsProductUsageResponse;
}) {
  const hasAnyUsage = usage.roles.some((r) => r.page_views > 0);

  if (!hasAnyUsage) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No usage of One is recorded for this window yet.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <RoleComparisonSection usage={usage} />
      <StaffTableSection usage={usage} />
      <ProductUsagePagesChart
        pages={usage.pages}
        role={usage.role as InsightsProductUsageRoleFilter}
      />
      <ProductUsageDailyChart daily={usage.daily} />
    </div>
  );
}

function RoleComparisonSection({
  usage,
}: {
  usage: InsightsProductUsageResponse;
}) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Role comparison
        </h2>
        <p className="text-muted-foreground text-sm">
          Page views, people, and views per person — always across all staff,
          whichever filter is selected below
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {usage.roles.map((row) => (
          <div
            key={row.role}
            className="border-border bg-surface rounded-lg border p-4"
          >
            <div className="text-foreground mb-3 text-sm font-semibold">
              {ROLE_FILTER_LABELS[row.role as InsightsProductUsageRoleFilter] ??
                row.role}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <StatCard
                label="Page views"
                value={formatCount(row.page_views)}
              />
              <StatCard label="People" value={formatCount(row.people)} />
              <StatCard
                label="Views/person"
                value={row.views_per_person.toFixed(1)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffTableSection({ usage }: { usage: InsightsProductUsageResponse }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">Staff</h2>
        <p className="text-muted-foreground text-sm">
          Page views, sessions, and days active — sorted by page views, narrowed
          to the selected filter
        </p>
      </div>
      <div className="border-border bg-surface overflow-hidden rounded-lg border">
        {usage.users.length === 0 ? (
          <div className="text-muted-foreground p-4 text-sm">
            No activity for this filter in this window yet.
          </div>
        ) : (
          <div className="max-h-[32rem] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Days active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.users.map((person) => (
                  <TableRow key={person.user_id}>
                    <TableCell className="font-medium">
                      {person.user_name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {businessRoleLabel(person.business_role)}
                    </TableCell>
                    <TableCell className="text-right">
                      {person.page_views}
                    </TableCell>
                    <TableCell className="text-right">
                      {person.sessions}
                    </TableCell>
                    <TableCell className="text-right">
                      {person.days_active}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
